import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { FaWhatsapp, FaMapMarked, FaInfoCircle } from 'react-icons/fa';
import { IoMdCall } from 'react-icons/io';
import { MdEmail } from 'react-icons/md';
import { IoImagesOutline } from 'react-icons/io5';
import { MdOutlineConnectWithoutContact } from 'react-icons/md';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import '../../style/FoundPetDetailsPage.css';
import { useTranslation } from 'react-i18next';
import petSpecies from '../../data/petSpecies';
import foundPetOutcomes from '../../data/foundPetOutcomes';
import i18n from 'i18next';

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 };

const FoundPetDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [foundPet, setFoundPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [view, setView] = useState('info'); // 'info', 'map', or 'images'
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsError, setSuggestionsError] = useState(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // Google Maps
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places', 'maps'],
    });

    // All hooks must be at the top level
    useEffect(() => {
        const fetchFoundPet = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/found-pets/${id}`);
                setFoundPet(response.data);
                // Check if current user is the owner
                const storedUser = JSON.parse(localStorage.getItem('user'));
                if (storedUser && storedUser.token && response.data.reportedBy) {
                    const jwtPayload = JSON.parse(atob(storedUser.token.split('.')[1]));
                    if (jwtPayload.id === response.data.reportedBy._id || jwtPayload._id === response.data.reportedBy._id) {
                        setIsOwner(true);
                    }
                }
            } catch (err) {
                setError(err.message || 'Error fetching found pet details');
            } finally {
                setLoading(false);
            }
        };
        fetchFoundPet();
    }, [id, baseUrl]);

    useEffect(() => {
        // Only fetch suggestions if foundPet is loaded and has location
        if (!foundPet || !foundPet.foundLocation || !foundPet.foundLocation.coordinates) return;
        const [lng, lat] = foundPet.foundLocation.coordinates;
        setSuggestionsLoading(true);
        setSuggestionsError(null);
        axios.get(`${baseUrl}/api/pets/lost-suggestions?lat=${lat}&lng=${lng}&petType=${foundPet.petType}`)
            .then(response => setSuggestions(response.data))
            .catch(err => setSuggestionsError(err.message || 'Error fetching suggestions'))
            .finally(() => setSuggestionsLoading(false));
    }, [foundPet, baseUrl]);

    // Early returns after all hooks
    if (loading) return <div style={{ textAlign: 'center' }}>{t('general.loading')}</div>;
    if (error) return <div style={{ color: 'red', textAlign: 'center' }}>{t('general.error')}: {error}</div>;
    if (!foundPet) return null;

    // Prepare map markers
    let foundMarker = null;
    let leftMarker = null;
    let mapCenter = DEFAULT_CENTER;
    if (foundPet.foundLocation && foundPet.foundLocation.coordinates && foundPet.foundLocation.coordinates.length === 2) {
        foundMarker = {
            lat: foundPet.foundLocation.coordinates[1],
            lng: foundPet.foundLocation.coordinates[0],
        };
        mapCenter = foundMarker;
    }
    if (foundPet.outcome === 'found_and_left_elsewhere' && foundPet.outcomeLocation && foundPet.outcomeLocation.coordinates && foundPet.outcomeLocation.coordinates.length === 2) {
        leftMarker = {
            lat: foundPet.outcomeLocation.coordinates[1],
            lng: foundPet.outcomeLocation.coordinates[0],
        };
    }

    // Contact info
    const contactName = foundPet.reportedBy?.name || foundPet.reporterName || 'Unknown';
    const contactEmail = foundPet.reportedBy?.email || foundPet.reporterEmail || null;
    const contactPhone = foundPet.reportedBy?.phoneNumber || foundPet.reporterPhoneNumber || null;

    // Format found date
    const foundDate = foundPet.foundDateTime ? new Date(foundPet.foundDateTime).toLocaleString() : 'Unknown';


    // Generic label translation for pet species and outcomes (as in ReportFoundPet.js)
    const getTranslatedLabel = (options, value) => {
        const option = options.find(opt => opt.value === value);
        return option ? option.label[i18n.language] || option.label.en : value;
    };

    // Outcome label using foundPetOutcomes
    const outcomeLabel = getTranslatedLabel(foundPetOutcomes, foundPet.outcome);

    // Images
    const images = Array.isArray(foundPet.images) ? foundPet.images : [];
    const profileImage = images[0] || null;
    const otherImages = images.slice(1);

    // Prepare image URLs for lightbox
    const imageUrls = images;

    return (
        <div className="found-pet-details-page">
            {/* Header Section */}
            <div className="found-pet-header">
                <div className="found-pet-header-left">
                    {profileImage ? (
                        <img
                            src={profileImage}
                            alt="Profile"
                            className="pet-profile-picture"
                        />
                    ) : (
                        <div className="placeholder-picture" style={{ color: '#8A8A8A', width: 135, height: 135, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '999px', background: '#FADBD7', fontSize: 32 }}>{t('generalPet.imageAvailable')}</div>
                    )}
                    <div className="pet-details">
                        <h1 className="pet-name">{foundPet.petType ? getTranslatedLabel(petSpecies, foundPet.petType) : t('foundPetDetailsPage.petType')}</h1>
                        {isOwner && (
                            <button
                                className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight"
                                onClick={() => navigate(`/found-pet/${id}/edit`)}
                            >
                                {t('foundPetDetailsPage.edit')}
                            </button>
                        )}
                    </div>
                </div>
                <div className="found-pet-header-right">
                    <p className="contact">{t('foundPetDetailsPage.contactInfo')}</p>
                    <div className="contact-icons">
                        {contactPhone && (
                            <a
                                href={`https://wa.me/${contactPhone.replace(/^[+]/, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="contact-icon whatsapp"
                            >
                                <FaWhatsapp />
                            </a>
                        )}
                        {contactPhone && (
                            <a
                                href={`tel:${contactPhone}`}
                                className="contact-icon call"
                            >
                                <IoMdCall />
                            </a>
                        )}
                        {contactEmail && (
                            <a
                                href={`mailto:${contactEmail}`}
                                className="contact-icon email"
                            >
                                <MdEmail />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* View Toggle Section */}
            <div className="section-navigation">
                <button
                    className={`nav-btn ${view === 'info' ? 'active' : ''}`}
                    onClick={() => setView('info')}
                    title={t('foundPetDetailsPage.info')}
                >
                    <FaInfoCircle />
                </button>
                <button
                    className={`nav-btn ${view === 'map' ? 'active' : ''}`}
                    onClick={() => setView('map')}
                    title={t('foundPetDetailsPage.map')}
                >
                    <FaMapMarked />
                </button>
                <button
                    className={`nav-btn ${view === 'images' ? 'active' : ''}`}
                    onClick={() => setView('images')}
                    title={t('foundPetDetailsPage.images')}
                >
                    <IoImagesOutline />
                </button>
                <button
                    className={`nav-btn ${view === 'suggestions' ? 'active' : ''}`}
                    onClick={() => setView('suggestions')}
                    title={t('foundPetDetailsPage.lostPetSuggestions') || 'Lost Pet Suggestions'}
                >
                    <MdOutlineConnectWithoutContact />
                </button>
            </div>
            {/* Suggestions Section */}
            {view === 'suggestions' && (
                <div className="lost-pet-suggestions-section" style={{ margin: '24px 0' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdOutlineConnectWithoutContact size={24} style={{ color: '#D35400' }} />
                        {t('foundPetDetailsPage.lostPetSuggestions') || 'Lost Pet Suggestions Nearby'}
                    </h3>
                    {suggestionsLoading && <div>{t('general.loading') || 'Loading suggestions...'}</div>}
                    {suggestionsError && <div style={{ color: 'red' }}>{suggestionsError}</div>}
                    {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && (
                        <div style={{ color: '#888' }}>{t('foundPetDetailsPage.noSuggestions') || 'No lost pets found nearby.'}</div>
                    )}
                    {!suggestionsLoading && !suggestionsError && suggestions.length > 0 && (
                        <ul className="lost-pet-suggestions-list" style={{ listStyle: 'none', padding: 0 }}>
                            {suggestions.map((pet) => {
                                // Get last lostInfo entry
                                const lastLostInfo = Array.isArray(pet.lostInfo) && pet.lostInfo.length > 0 ? pet.lostInfo[pet.lostInfo.length - 1] : {};
                                const petImage = Array.isArray(pet.images) && pet.images.length > 0 ? pet.images[0].url || pet.images[0] : null;
                                return (
                                    <li key={pet._id} className="lost-pet-suggestion-item" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: '#FDF6E3', borderRadius: 8, padding: 12 }}>
                                        {petImage ? (
                                            <img src={petImage} alt={pet.name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '50%' }} />
                                        ) : (
                                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#FADBD7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A8A8A', fontSize: 24 }}>?</div>
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{pet.name}</div>
                                            <div style={{ color: '#888' }}>{getTranslatedLabel(petSpecies, pet.petType)}</div>
                                            <div style={{ fontSize: 13, color: '#666' }}>{lastLostInfo.lastSeenDate ? `${t('foundPetDetailsPage.lastSeen') || 'Last seen'}: ${new Date(lastLostInfo.lastSeenDate).toLocaleString()}` : ''}</div>
                                        </div>
                                        <button className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight" onClick={() => navigate(`/lost-pet/${pet._id}`)}>
                                            {t('foundPetDetailsPage.viewDetails') || 'View Details'}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Info Section */}
            {view === 'info' && (
                <div className="found-details">
                    <h3>{t('foundPetDetailsPage.foundDetails')}</h3>
                    <p><strong>{t('foundPetDetailsPage.foundOn')}</strong> {foundDate}</p>
                    <p><strong>{t('foundPetDetailsPage.outcome')}</strong> {outcomeLabel}</p>
                    <p><strong>{t('foundPetDetailsPage.description')}</strong> {foundPet.description || t('foundPetDetailsPage.noDescription')}</p>
                    {foundPet.foundLocation?.address && <p><strong>{t('foundPetDetailsPage.foundAt')}</strong> {foundPet.foundLocation.address}</p>}
                    {foundPet.outcome === 'found_and_left_elsewhere' && foundPet.outcomeLocation?.address && (
                        <p><strong>{t('foundPetDetailsPage.leftAt')}</strong> {foundPet.outcomeLocation.address}</p>
                    )}
                </div>
            )}

            {/* Map Section */}
            {view === 'map' && (
                <div style={{ marginTop: 10 }}>
                    {loadError && <div style={{ color: 'red', textAlign: 'center' }}>{t('reportPetLostPage.error.mapLoadFailed')}: {loadError.message}</div>}
                    {isLoaded && foundMarker && (
                        <GoogleMap
                            mapContainerStyle={MAP_CONTAINER_STYLE}
                            center={mapCenter}
                            zoom={15}
                            onLoad={map => {
                                // If both markers exist, fit bounds
                                if (foundMarker && leftMarker) {
                                    const bounds = new window.google.maps.LatLngBounds();
                                    bounds.extend(foundMarker);
                                    bounds.extend(leftMarker);
                                    map.fitBounds(bounds);
                                } else if (foundMarker) {
                                    map.setCenter(foundMarker);
                                    map.setZoom(15);
                                }
                            }}
                        >
                            {/* Red marker: where found */}
                            <Marker position={foundMarker} label="F" title={t('foundPetDetailsPage.foundAt')} icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }} />
                            {/* Blue marker: where left, if applicable */}
                            {leftMarker && <Marker position={leftMarker} label="L" title={t('foundPetDetailsPage.leftAt')} icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' }} />}
                        </GoogleMap>
                    )}
                </div>
            )}

            {/* Images Section */}
            {view === 'images' && images.length > 0 && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                        <button
                            className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight"
                            style={{ minWidth: 100 }}
                            onClick={() => navigate(`/found-pet/${foundPet._id}/manage-images`)}
                        >
                            {t('foundPetDetailsPage.edit')}
                        </button>
                    </div>
                    <div className="images-section">
                        {images.map((img, idx) => (
                            <img
                                key={idx}
                                src={img}
                                alt={t('foundPetDetailsPage.imageAlt', { index: idx + 1 })}
                                onClick={() => {
                                    setCurrentImageIndex(idx);
                                    setIsLightboxOpen(true);
                                }}
                            />
                        ))}
                    </div>
                </>
            )}

            {isLightboxOpen && (
                <Lightbox
                    mainSrc={imageUrls[currentImageIndex]}
                    nextSrc={imageUrls[(currentImageIndex + 1) % imageUrls.length]}
                    prevSrc={imageUrls[(currentImageIndex + imageUrls.length - 1) % imageUrls.length]}
                    onCloseRequest={() => setIsLightboxOpen(false)}
                    onMovePrevRequest={() =>
                        setCurrentImageIndex((currentImageIndex + imageUrls.length - 1) % imageUrls.length)
                    }
                    onMoveNextRequest={() =>
                        setCurrentImageIndex((currentImageIndex + 1) % imageUrls.length)
                    }
                />
            )}
        </div>
    );
};

export default FoundPetDetailsPage;